import React, { useRef, useEffect, useState } from 'react';
import './App.css';
import Home from './components/Home';
import Skills from './components/Skills';
import Experiences from './components/Experiences';
import Projects from './components/Projects';
import Certifications from './components/Certifications';
import Gallery from './components/Gallery';
import Antigravity from './components/Antigravity';
import Navbar from './components/Navbar';

function App() {
  const scrollContainerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    let targetScroll = scrollContainer.scrollLeft;
    let currentScroll = scrollContainer.scrollLeft;
    let animationFrameId;

    const handleWheel = (evt) => {
      // Only prevent default if we are scrolling vertically (deltaY)
      if (evt.deltaY === 0) return;

      evt.preventDefault();

      // If animation is not running, sync with current DOM state
      if (!animationFrameId) {
        currentScroll = scrollContainer.scrollLeft;
        targetScroll = currentScroll;
      }

      targetScroll += evt.deltaY;

      // Clamp existing targetScroll to boundaries
      const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
      targetScroll = Math.max(0, Math.min(targetScroll, maxScroll));

      if (!animationFrameId) {
        animationFrameId = requestAnimationFrame(autoScroll);
      }
    };

    const autoScroll = () => {
      currentScroll += (targetScroll - currentScroll) * 0.1; // Lerp for smooth scrolling
      scrollContainer.scrollLeft = currentScroll;

      if (Math.abs(targetScroll - currentScroll) > 0.1) {
        animationFrameId = requestAnimationFrame(autoScroll);
      } else {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false }); // Use passive: false to allow preventDefault

    return () => {
      window.removeEventListener('wheel', handleWheel);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);



  // Mouse tracking for Antigravity
  useEffect(() => {
    if (isMobile) return; // Skip mouse tracking on mobile

    const handleMouseMove = (e) => {
      // Calculate normalized coordinates (-1 to 1)
      const x = (e.clientX / window.innerWidth) * 2 - 1;
      const y = -(e.clientY / window.innerHeight) * 2 + 1;

      // Dispatch custom event for Antigravity to listen to
      window.dispatchEvent(new CustomEvent('mousePosition', {
        detail: { x, y }
      }));
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isMobile]);

  return (
    <div className="App">
      {/* Navbar */}
      <Navbar />

      {/* Fixed Antigravity Background - Only on desktop */}
      {!isMobile && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 1,
          pointerEvents: 'none'
        }}>
          <Antigravity
            count={300}
            magnetRadius={10}
            ringRadius={10}
            waveSpeed={0.4}
            waveAmplitude={1}
            particleSize={2}
            lerpSpeed={0.1}
            color="#00D4FF"
            autoAnimate={false}
            particleVariance={1}
            rotationSpeed={0}
            depthFactor={1}
            pulseSpeed={3}
            particleShape="capsule"
            fieldStrength={10}
          />
        </div>
      )}

      {/* Content with higher z-index in foreground, but pointer-events none to allow interaction with background */}
      <div
        className="horizontal-container"
        ref={scrollContainerRef}
        style={{
          position: 'relative',
          zIndex: 10,
          pointerEvents: 'none'
        }}
      >
        <Home />
        <Skills />
        <Experiences />
        <Projects />
        <Certifications />
        <Gallery />
      </div>
    </div>
  );
}

export default App;
